import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Xmark } from '@gravity-ui/icons';
import NumberedPagination from '../../../components/NumberedPagination/NumberedPagination';
import { usePolling } from '../../../hooks/usePolling';
import { useCameraNames } from '../../../hooks/useCameraNames';
import {
  cancelDuplicateAnalysis,
  decideDuplicateCandidate,
  getDuplicateAnalysisJob,
  getDuplicateCapabilities,
  getDuplicateReviewSummary,
  listDuplicateCandidates,
  startDuplicateAnalysis,
} from '../../../services/duplicates';
import { describeUserError } from '../../../utils/apiError';
import { withCameraWord } from '../../../utils/hierarchyLabels';
import { countRu, EVENT_FORMS, PAIR_FORMS } from '../../../utils/pluralRu';
import { listUnifiedEvents } from '../../../services/violations';
import { selectionSignature } from './eventsStatsModel';
import { collectEventIds } from './eventsQueryModel';
import {
  CANDIDATES_PAGE_SIZE,
  JOB_ACTIVE_STATUSES,
  JOB_POLL_INTERVAL_MS,
  DUPLICATES_UNAVAILABLE_TEXT,
  buildCandidateParams,
  buildDuplicateJobRequest,
  DUPLICATE_MAX_COLLECTED,
  describeCandidate,
  describeDuplicateCapabilities,
  describeStartError,
  formatReviewPrecision,
  isDuplicatesDisabledError,
  withCollectedIds,
} from './duplicatesModel';

/*
 * Блок «Возможные дубли» рядом с панелью «Посчитать».
 *
 * Анализ идёт на backend: запускаем задание, опрашиваем его через usePolling,
 * «Отменить» останавливает. Кандидаты листаются по 10, решение «Дубль» /
 * «Не дубль» сохраняется сразу. Открытость блока, номер задания и страница
 * кандидатов лежат в extras выбора и переживают Detail/Back; задание привязано
 * к подписи выбора, при смене выбора прежний ход анализа не показывается.
 *
 * FE-CORR-07 DUP-001: при открытии блока читаются capabilities. Запуск
 * доступен только при job_creation_enabled=true; ошибка capabilities или 503
 * DUPLICATES01_DISABLED на запуске блокируют его без повторов. Сохранённые
 * пары остаются доступны, пока review_enabled не выключен явно.
 */

const POLL_MAX_FAILURES = 3;

const STATE_LABELS = {
  confirmed_duplicate: 'Дубль',
  rejected_duplicate: 'Не дубль',
};

const JobStatus = ({ job, onCancel, cancelling }) => {
  const active = JOB_ACTIVE_STATUSES.includes(job.status);
  return (
    <div className="vio-dup-job">
      {active && (
        <>
          <p className="vio-field-hint">
            {job.status === 'cancel_requested'
              ? 'Останавливаем анализ…'
              : `Анализируем… ${job.processed_count} из ${job.selected_count}`}
          </p>
          <button
            type="button"
            className="vio-btn"
            onClick={onCancel}
            disabled={cancelling || job.status === 'cancel_requested'}
          >
            Отменить
          </button>
        </>
      )}
      {job.status === 'completed' && (
        <p className="vio-field-hint">
          {job.candidate_count > 0
            ? `Найдено ${countRu(job.candidate_count, PAIR_FORMS)}`
            : 'Похожих событий не найдено'}
        </p>
      )}
      {job.status === 'cancelled' && <p className="vio-field-hint">Анализ отменён</p>}
      {job.status === 'failed' && (
        <p className="vio-field-hint vio-field-hint--error">Не удалось завершить анализ. Запустите его ещё раз.</p>
      )}
      {!active && job.skipped_no_evidence > 0 && (
        <p className="vio-field-hint">
          {`У ${countRu(job.skipped_no_evidence, EVENT_FORMS)} нет снимка, их не сравнивали`}
        </p>
      )}
      {!active && job.skipped_storage > 0 && (
        <p className="vio-field-hint">
          {`Не удалось прочитать снимки у ${countRu(job.skipped_storage, EVENT_FORMS)}`}
        </p>
      )}
    </div>
  );
};

// Нарушения выборки по фильтру, который не укладывается в source_filter задания.
const listViolationsForDuplicates = (params) => listUnifiedEvents(params);

const DuplicatesPanel = ({ selection, ruleNames = {}, onOpenPair, listViolations = listViolationsForDuplicates }) => {
  const { mode, ids, types, filters, count, extras, setExtras } = selection;
  const signature = selectionSignature({ mode, ids, filters, count });
  const saved = extras?.duplicates || {};
  const open = Boolean(saved.open);
  const fresh = saved.signature === signature;
  const jobId = fresh ? saved.jobId || null : null;
  const page = Number.isFinite(saved.page) && saved.page >= 0 ? saved.page : 0;

  const request = useMemo(
    () => buildDuplicateJobRequest({ mode, ids, types, filters }),
    [mode, ids, types, filters]
  );
  const candidateParams = useMemo(
    () => buildCandidateParams({ mode, filters }),
    [mode, filters]
  );
  const paramsKey = JSON.stringify(candidateParams);

  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [jobError, setJobError] = useState('');
  const [candidates, setCandidates] = useState({ items: [], total: 0 });
  const [listState, setListState] = useState('idle');
  const [listError, setListError] = useState('');
  const [summary, setSummary] = useState(null);
  const [decidingId, setDecidingId] = useState(null);
  const [decideError, setDecideError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const prevStatusRef = useRef(null);
  const jobRef = useRef(null);
  const inFlightRef = useRef(false);
  const currentJobRef = useRef({ open: false, jobId: null, signature: null });
  currentJobRef.current = { open, jobId, signature };
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  const failuresRef = useRef(0);
  const [pollStopped, setPollStopped] = useState(false);
  const [summaryError, setSummaryError] = useState(false);
  const [capabilities, setCapabilities] = useState({ state: 'loading', data: null, error: null });
  const [disabledByServer, setDisabledByServer] = useState(false);
  const capabilitiesRequestedRef = useRef(false);
  jobRef.current = job;

  // Один запрос на жизнь страницы: при ошибке не повторяем, запуск остаётся закрытым.
  useEffect(() => {
    if (!open || capabilitiesRequestedRef.current) return;
    capabilitiesRequestedRef.current = true;
    getDuplicateCapabilities()
      .then((data) => {
        if (mountedRef.current) setCapabilities({ state: 'ready', data, error: null });
      })
      .catch((error) => {
        if (mountedRef.current) setCapabilities({ state: 'error', data: null, error });
      });
  }, [open]);

  const access = describeDuplicateCapabilities(capabilities);
  const canCreate = access.canCreate && !disabledByServer;
  const reviewEnabled = access.reviewEnabled;
  // Список пар ждёт capabilities: при review_enabled=false он не запрашивается вовсе.
  const reviewReady = capabilities.state !== 'loading' && reviewEnabled;

  const setPage = useCallback((nextPage) => {
    setExtras({ duplicates: { open, signature, jobId, page: nextPage } });
  }, [setExtras, open, signature, jobId]);

  // Опрос задания: один запрос за раз, три неудачи подряд останавливают опрос,
  // 404/403 значит, что задания больше нет.
  const pollJob = useCallback((id) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    getDuplicateAnalysisJob(id)
      .then((response) => {
        if (currentJobRef.current.jobId !== id) return;
        failuresRef.current = 0;
        setJobError('');
        setJob(response);
      })
      .catch((error) => {
        // Ответ про задание, которое уже не текущее (панель закрыта или запущено другое), игнорируется.
        if (currentJobRef.current.jobId !== id) return;
        const status = error?.response?.status;
        if (status === 404 || status === 403) {
          setPollStopped(false);
          setJob(null);
          setJobError('Задание больше недоступно. Запустите поиск заново.');
          if (currentJobRef.current.open) {
            setExtras({ duplicates: { open: true, signature, jobId: null, page } });
          }
          return;
        }
        failuresRef.current += 1;
        if (failuresRef.current >= POLL_MAX_FAILURES) {
          setPollStopped(true);
          setJobError('Не удалось получить ход анализа. Проверьте соединение.');
        } else {
          setJobError(describeUserError(error, 'Не удалось узнать, как идёт анализ'));
        }
      })
      .finally(() => { inFlightRef.current = false; });
  }, [setExtras, signature, page]);

  // Состояние задания после возврата с Detail подтягивается заново.
  useEffect(() => {
    failuresRef.current = 0;
    setPollStopped(false);
    // Ошибка предыдущего задания не переходит на новое; сообщение про пропавшее задание остаётся.
    if (jobId) setJobError('');
    if (!open || !jobId) {
      setJob(null);
      return;
    }
    // Только что запущенное задание уже в состоянии, повторный запрос не нужен.
    if (jobRef.current?.job_id === jobId) return;
    pollJob(jobId);
    // pollJob зависит от страницы и подписи, а перечитывать задание нужно только при смене номера.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, jobId]);

  const polling = open && Boolean(jobId) && Boolean(job) && !pollStopped
    && JOB_ACTIVE_STATUSES.includes(job.status);
  usePolling(() => pollJob(jobId), JOB_POLL_INTERVAL_MS, polling);

  const retryPoll = () => {
    failuresRef.current = 0;
    setPollStopped(false);
    setJobError('');
    pollJob(jobId);
  };

  // Анализ завершился или отменён (частичный результат) — кандидаты и сводка перечитываются.
  useEffect(() => {
    const status = job?.status || null;
    if (
      prevStatusRef.current
      && JOB_ACTIVE_STATUSES.includes(prevStatusRef.current)
      && (status === 'completed' || status === 'cancelled')
    ) {
      setReloadKey((key) => key + 1);
    }
    prevStatusRef.current = status;
  }, [job]);

  // Фильтр изменился — прежняя страница кандидатов относится к другому списку.
  const prevParamsKeyRef = useRef(paramsKey);
  useEffect(() => {
    if (prevParamsKeyRef.current === paramsKey) return;
    prevParamsKeyRef.current = paramsKey;
    if (page !== 0) setPage(0);
  }, [paramsKey, page, setPage]);

  useEffect(() => {
    if (!open || !reviewReady) return undefined;
    let cancelled = false;
    setListState('loading');
    setListError('');
    listDuplicateCandidates({ ...candidateParams, limit: CANDIDATES_PAGE_SIZE, offset: page * CANDIDATES_PAGE_SIZE })
      .then((response) => {
        if (cancelled) return;
        setCandidates({ items: response.items, total: response.total });
        setListState('ready');
      })
      .catch((error) => {
        if (cancelled) return;
        setListError(describeUserError(error, 'Не удалось загрузить возможные дубли'));
        setListState('error');
      });
    return () => { cancelled = true; };
    // candidateParams меняется вместе с paramsKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reviewReady, paramsKey, page, reloadKey]);

  useEffect(() => {
    if (!open || !reviewReady) return undefined;
    let cancelled = false;
    setSummaryError(false);
    getDuplicateReviewSummary(candidateParams)
      .then((response) => { if (!cancelled) setSummary(response); })
      .catch(() => {
        if (!cancelled) {
          setSummary(null);
          setSummaryError(true);
        }
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reviewReady, paramsKey, reloadKey]);

  const { cameraLabel, cameraTimezone } = useCameraNames(candidates.items);

  const start = () => {
    if (!canCreate || !request.available || starting) return;
    const requestSignature = signature;
    setStarting(true);
    setJobError('');
    // Фильтр, который не укладывается в source_filter: сначала собираем id нарушений.
    const resolved = request.deferred
      ? collectEventIds(listViolations, request.listParams, DUPLICATE_MAX_COLLECTED).then(withCollectedIds)
      : Promise.resolve(request);
    resolved
      .then((ready) => {
        if (!ready.available) {
          const failure = new Error(ready.reason);
          failure.userReason = ready.reason;
          throw failure;
        }
        return startDuplicateAnalysis(ready.body);
      })
      .then((response) => {
        // Ответ на устаревший запуск (выбор изменился, панель закрыта или размонтирована) не применяется.
        if (!mountedRef.current) return;
        if (!currentJobRef.current.open || currentJobRef.current.signature !== requestSignature) return;
        setJob(response);
        setExtras({ duplicates: { open: true, signature: requestSignature, jobId: response.job_id, page: 0 } });
      })
      .catch((error) => {
        if (!mountedRef.current) return;
        // Выключенный поиск — не сбой: причина показывается вместо ошибки, повтора нет.
        if (isDuplicatesDisabledError(error)) {
          setDisabledByServer(true);
          return;
        }
        setJobError(error?.userReason || describeStartError(error));
      })
      .finally(() => setStarting(false));
  };

  const cancel = () => {
    if (!jobId || cancelling) return;
    setCancelling(true);
    cancelDuplicateAnalysis(jobId)
      .then(setJob)
      .catch((error) => setJobError(describeUserError(error, 'Не удалось отменить анализ')))
      .finally(() => setCancelling(false));
  };

  const decide = (candidate, state) => {
    if (decidingId !== null) return;
    setDecidingId(candidate.candidate_id);
    setDecideError('');
    decideDuplicateCandidate(candidate.candidate_id, state)
      .then((updated) => {
        setCandidates((prev) => ({
          ...prev,
          items: prev.items.map((item) => (item.candidate_id === updated.candidate_id ? updated : item)),
        }));
        setReloadKey((key) => key + 1);
      })
      .catch((error) => setDecideError(describeUserError(error, 'Не удалось сохранить решение')))
      .finally(() => setDecidingId(null));
  };

  const close = () => {
    setJobError('');
    setExtras({ duplicates: { open: false, signature: null, jobId: null, page: 0 } });
  };

  if (count <= 0) return null;

  if (!open) {
    return (
      <section className="vio-card vio-stats vio-dup" aria-label="Возможные дубли">
        <div className="vio-stats-head">
          <button
            type="button"
            className="vio-btn"
            onClick={() => setExtras({ duplicates: { open: true, signature, jobId: null, page: 0 } })}
          >
            Возможные дубли
          </button>
        </div>
      </section>
    );
  }

  const jobActive = Boolean(job) && JOB_ACTIVE_STATUSES.includes(job.status);
  // Одна причина рядом с кнопкой: сначала доступность поиска, затем выбор событий.
  const startReason = disabledByServer
    ? DUPLICATES_UNAVAILABLE_TEXT
    : access.reason || (canCreate && !request.available ? request.reason : null);

  return (
    <section className="vio-card vio-stats vio-dup" aria-label="Возможные дубли">
      <div className="vio-stats-head">
        <h3 className="vio-stats-title">Возможные дубли</h3>
        <span className="vio-stats-actions">
          <button
            type="button"
            className="vio-btn vio-btn--primary"
            onClick={start}
            disabled={!canCreate || !request.available || starting || jobActive}
          >
            {starting ? 'Запускаем…' : 'Найти дубли'}
          </button>
          <button
            type="button"
            className="vio-btn vio-btn--icon"
            onClick={close}
            title="Закрыть возможные дубли"
            aria-label="Закрыть возможные дубли"
          >
            <Xmark width={16} height={16} />
          </button>
        </span>
      </div>

      {startReason && <p className="vio-field-hint">{startReason}</p>}
      {!startReason && request.note && <p className="vio-field-hint">{request.note}</p>}
      {jobError && <p className="vio-field-hint vio-field-hint--error">{jobError}</p>}
      {jobId && jobError && (pollStopped || !job) && (
        <button type="button" className="vio-btn" onClick={retryPoll}>Повторить</button>
      )}
      {job && jobId && <JobStatus job={job} onCancel={cancel} cancelling={cancelling} />}

      {summaryError && (
        <p className="vio-field-hint vio-field-hint--error">Не удалось загрузить точность по проверке</p>
      )}
      {summary && summary.total_candidates > 0 && (
        <div className="vio-stats-kpis">
          <div className="vio-stats-kpi">
            <span className="vio-stats-kpi-label">Точность по проверке</span>
            <span className="vio-stats-kpi-value">{formatReviewPrecision(summary.review_precision_percent)}</span>
            {summary.reviewed_count > 0 && (
              <span className="vio-stats-kpi-hint">
                {`Рассмотрено ${summary.reviewed_count} из ${summary.total_candidates}`}
              </span>
            )}
          </div>
        </div>
      )}

      {listState === 'loading' && <p className="vio-field-hint">Загружаем…</p>}
      {listState === 'error' && <p className="vio-field-hint vio-field-hint--error">{listError}</p>}
      {decideError && <p className="vio-field-hint vio-field-hint--error">{decideError}</p>}
      {!reviewEnabled && <p className="vio-field-hint">Просмотр сохранённых пар недоступен</p>}
      {reviewEnabled && listState === 'ready' && candidates.items.length === 0 && (
        <p className="vio-field-hint">
          {canCreate ? 'Возможных дублей пока нет' : 'Сохранённых пар для проверки нет'}
        </p>
      )}

      {reviewEnabled && candidates.items.length > 0 && (
        <ul className="vio-dup-list">
          {candidates.items.map((candidate) => {
            const { title, time } = describeCandidate(candidate, {
              cameraName: withCameraWord(cameraLabel(candidate.camera_id)),
              ruleName: ruleNames[candidate.rule_id],
              timezone: cameraTimezone(candidate.camera_id),
            });
            const stateLabel = STATE_LABELS[candidate.state];
            const busy = decidingId === candidate.candidate_id;
            return (
              <li key={candidate.candidate_id} className="vio-dup-row">
                <div className="vio-dup-text">
                  <span className="vio-cell-title">{title}</span>
                  {time && <span className="vio-cell-sub">{time}</span>}
                </div>
                {stateLabel && (
                  <span className={`vio-badge ${candidate.state === 'confirmed_duplicate' ? 'vio-badge--duplicate' : ''}`}>
                    {stateLabel}
                  </span>
                )}
                <span className="vio-dup-actions">
                  <button type="button" className="vio-btn vio-btn--link" onClick={() => onOpenPair?.(candidate)}>
                    Открыть пару
                  </button>
                  <button
                    type="button"
                    className="vio-btn"
                    disabled={busy || candidate.state === 'confirmed_duplicate'}
                    onClick={() => decide(candidate, 'confirmed_duplicate')}
                  >
                    Дубль
                  </button>
                  <button
                    type="button"
                    className="vio-btn"
                    disabled={busy || candidate.state === 'rejected_duplicate'}
                    onClick={() => decide(candidate, 'rejected_duplicate')}
                  >
                    Не дубль
                  </button>
                </span>
              </li>
            );
          })}
        </ul>
      )}

      <NumberedPagination
        total={candidates.total}
        limit={CANDIDATES_PAGE_SIZE}
        offset={page * CANDIDATES_PAGE_SIZE}
        pageSizes={[CANDIDATES_PAGE_SIZE]}
        onOffsetChange={(nextOffset) => setPage(Math.floor(nextOffset / CANDIDATES_PAGE_SIZE))}
      />
    </section>
  );
};

export default DuplicatesPanel;
